import React, {useContext, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import LocalPharmacyRoundedIcon from '@mui/icons-material/LocalPharmacyRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {useLocation, useNavigate} from "react-router-dom";
import {UserContext} from "../../hooks/userContext";
import {useLazyQueryOrders} from "../../queries/order/hooks/useLazyQueryOrders";
import {routeConfigs} from "../../navigation/routeConfig";

const INK_SOFT = '#475569';
const INK_ACTIVE = '#0F172A';
const PRIMARY = '#2563EB';
const COUNT_BADGE = '#F97316';
const ACTIVE_BG = '#F1F5F9';
const HOVER_BG = '#F1F5F9';
const BODY_FONT = '"Source Sans 3", sans-serif';
const MENU_ICON_SIZE = 24; // 20px (small) * 1.2
const MENU_TEXT_SIZE = 14; // sub-item label size
const MENU_SECTION_SIZE = 13.2; // 11 * 1.2
const MENU_ITEM_HEIGHT = 40;
const MENU_ICON_GAP = 43.2; // 36 * 1.2

const menuIconSx = { fontSize: MENU_ICON_SIZE };

interface INavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  showCount?: boolean;
}

interface INavSection {
  title: string;
  items: INavItem[];
}

const ADMIN_SECTIONS: INavSection[] = [
  {
    title: 'Manage',
    items: [
      { label: 'Pharmacies', path: '/pharmacies', icon: <LocalPharmacyRoundedIcon sx={menuIconSx} /> },
      { label: 'Postings', path: '/postings', icon: <SellRoundedIcon sx={menuIconSx} /> },
      { label: 'Sales', path: '/sales', icon: <PaidRoundedIcon sx={menuIconSx} /> },
      { label: 'Orders', path: '/orders', icon: <FactCheckRoundedIcon sx={menuIconSx} /> },
    ],
  },
];

const PHARMACY_SECTIONS: INavSection[] = [
  {
    title: 'Marketplace',
    items: [
      { label: 'Buy', path: '/', icon: <StorefrontRoundedIcon sx={menuIconSx} /> },
      { label: 'Sell', path: '/sell', icon: <SellRoundedIcon sx={menuIconSx} /> },
      { label: 'Wishlist', path: '/wishlist', icon: <FavoriteBorderRoundedIcon sx={menuIconSx} /> },
    ],
  },
  {
    title: 'Orders',
    items: [
      { label: 'Sell Orders', path: '/open-orders', icon: <AssignmentRoundedIcon sx={menuIconSx} />, showCount: true },
      { label: 'Buy History', path: '/buy-history', icon: <ReceiptLongRoundedIcon sx={menuIconSx} /> },
      { label: 'Sell History', path: '/sell-history', icon: <PaidRoundedIcon sx={menuIconSx} /> },
    ],
  },
  {
    title: 'Insights',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <DashboardRoundedIcon sx={menuIconSx} /> },
      { label: 'Settings', path: routeConfigs.settings.path, icon: <SettingsRoundedIcon sx={menuIconSx} /> },
    ],
  },
];

interface IMainListItemsProps {
  isAdmin: boolean;
  open?: boolean;
}

const MainListItems:React.FunctionComponent<IMainListItemsProps> = ({
  isAdmin,
  open = true
}) => {
  const { id }: any = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [count, setCount] = useState<number>(0);

  const [getOrders, { data }] = useLazyQueryOrders({
    variables: {
      where: {
        AND: [
          { sellerUserId: {equals: +id} },
          { isConfirmed: {equals: false }},
          { status: {equals: 1}}
        ]
      },
    },
    fetchPolicy: 'no-cache'
  });

  const getCount = (items: any) => {
    if (!items) {
      return 0;
    }

    let newData: any = [];

    items.forEach((item: any) => {
      const foundOrderIndex = newData.findIndex((dataItem: any) => dataItem.orderNumber === item.orderNumber);

      if (foundOrderIndex === -1) {
        newData.push(item);
      } else {
        newData[foundOrderIndex] = item;
      }
    });

    return newData.length;
  }

  useEffect(() => {
    if (isAdmin) return;

    getOrders();
    const interval = setInterval(() => {
      getOrders();
    }, 5000);

    return () => clearInterval(interval);
  }, [getOrders, isAdmin]);

  useEffect(() => {
    if (data?.findManyOrder) {
      setCount(getCount(data?.findManyOrder));
    }
  }, [data?.findManyOrder]);

  const isActive = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const sections = isAdmin ? ADMIN_SECTIONS : PHARMACY_SECTIONS;

  return (
    <React.Fragment>
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={section.title}>
          {open ? (
            <Box
              sx={{
                px: 2.75,
                pt: sectionIndex === 0 ? 1 : 2.5,
                pb: 0.75,
                fontSize: MENU_SECTION_SIZE,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: INK_SOFT,
                fontFamily: BODY_FONT,
                whiteSpace: 'nowrap',
              }}
            >
              {section.title}
            </Box>
          ) : (
            sectionIndex > 0 && <Box sx={{ mx: 1.5, my: 1.25, borderTop: '1px solid #E2E8F0' }} />
          )}
          {section.items.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: '8px',
                  mx: 1.25,
                  mb: 0.25,
                  px: 1.5,
                  minHeight: MENU_ITEM_HEIGHT,
                  flexGrow: 0,
                  color: active ? INK_ACTIVE : INK_SOFT,
                  bgcolor: active ? ACTIVE_BG : 'transparent',
                  transition: 'background-color .15s ease, color .15s ease, transform .2s ease',
                  '&:hover': {
                    bgcolor: active ? ACTIVE_BG : HOVER_BG,
                    transform: 'translateX(6px)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: MENU_ICON_GAP, color: active ? PRIMARY : INK_SOFT }}>
                  {item.showCount ? (
                    <Badge
                      variant="dot"
                      invisible={!count || open}
                      sx={{ '& .MuiBadge-badge': { bgcolor: COUNT_BADGE } }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontSize: MENU_TEXT_SIZE,
                    fontWeight: active ? 700 : 500,
                    fontFamily: BODY_FONT,
                    color: active ? INK_ACTIVE : INK_SOFT,
                  }}
                />
                {item.showCount && count > 0 && open && (
                  <Box
                    sx={{
                      ml: 1,
                      px: 0.9,
                      py: 0.4,
                      borderRadius: '10px',
                      bgcolor: COUNT_BADGE,
                      color: '#FFFFFF',
                      fontSize: MENU_SECTION_SIZE,
                      fontWeight: 700,
                      lineHeight: 1,
                      fontFamily: BODY_FONT,
                    }}
                  >
                    {count > 99 ? '99+' : count}
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </React.Fragment>
      ))}
    </React.Fragment>
  )
};

export default MainListItems;
